import { memo, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KentuckyKYC, SeonPlugin } from "@leapfinancial/kentucky-sdk";
import { createKentuckyPostHogBridge } from "../analytics";
import { config } from "../config";
import { APP_ROUTES } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useKentuckySdkStyles } from "../hooks/useKentuckySdkStyles";
import { useExitToPartnerOrLanding } from "../hooks/useExitToPartnerOrLanding";
import { forgetCipTicketId, rememberCipTicketId } from "../utils/chunkReload";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";

interface CipPageLocationState {
  cipTicketId: string;
}

/**
 * Embedded Kentucky CIP step.
 *
 * Reached from the transfer screen only when `cip-readiness` reported that the
 * user must verify their identity before the operation can be committed. It
 * mounts the Kentucky SDK against the provisioned `cipTicketId`; on success it
 * returns to the sending-money screen (which creates the operation), and on
 * error it routes to the error page.
 */
export const CipPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transfer" }} requireOccupant>
    <CipPageContent />
  </RequireActiveClerkSession>
));

const CipPageContent = memo(() => {
  // Scope SDK CSS to this screen only (removed on navigate away).
  useKentuckySdkStyles();
  useClaimLink();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { session } = useSessionStore();
  const { liveCallbackUrl } = useExitToPartnerOrLanding();

  const state = location.state as CipPageLocationState | null;
  const cipTicketId = rememberCipTicketId(state?.cipTicketId);

  // Theme comes from the claimed link stored in session.
  const partnerTheme = session.data?.partner?.theme;

  const plugins = useMemo(
    () => [createKentuckyPostHogBridge(), SeonPlugin()],
    [],
  );

  if (!cipTicketId) {
    navigate(APP_ROUTES.ERROR_PAGE, {
      state: {
        title: t("error.title"),
        subTitle: null,
        message: t("error.tryAgainLater"),
        btnText: t("common.retry"),
        isOut: false,
        pageUrl: APP_ROUTES.TRANSFER_PAGE,
      },
    });
    return null;
  }

  return (
    <KentuckyKYC
      ticketId={cipTicketId}
      apiBaseUrl={config.KENTUCKY_API_BASE_URL}
      googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
      awsRegion={config.AWS_REGION || "us-east-1"}
      theme={partnerTheme}
      plugins={plugins}
      onComplete={(result) => {
        console.log("[CipPage] KYC flow completed:", result);
        forgetCipTicketId();
        const current = useSessionStore.getState().session.data;
        const prompt = shouldPromptPhone(current, current?.linkId);
        if (prompt) {
          redirectTo(APP_ROUTES.PHONE_PAGE, {
            replace: true,
            state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
          });
          return;
        }
        const linkId = current?.linkId;
        void (linkId
          ? ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en")
          : Promise.resolve()
        ).then(() => {
          redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, {
            replace: true,
            state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
          });
        });
      }}
      onError={(error) => {
        console.error("[CipPage] KYC flow error:", error);
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: liveCallbackUrl
            ? {
                title: t("error.title"),
                subTitle: null,
                message: error.message || t("error.tryAgainLater"),
                btnText: t("error.returnToApp"),
                isOut: true,
                pageUrl: liveCallbackUrl,
              }
            : {
                title: t("error.title"),
                subTitle: null,
                message: error.message || t("error.tryAgainLater"),
                btnText: t("common.retry"),
                isOut: false,
                pageUrl: APP_ROUTES.TRANSFER_PAGE,
              },
        });
      }}
    />
  );
});
